/**
 * ⌘K / Ctrl K toggles search (A2 `search-overlay.tsx:28-42`).
 *
 * - Closed: opens `overlay=search` (a PUSH), clearing the profile's
 *   `section`/`edit` so search never opens on top of a stale editor.
 * - Open: closes it through `closeLayer`, the same path as Back.
 * - While typing in a field it does nothing — Ctrl K is a browser and a
 *   text-editing chord there — EXCEPT inside the search overlay itself,
 *   whose own input is where a person reaches for it to close.
 *
 * Mounted once, by the layout. The listener reads the search through a ref,
 * so it is attached once and never re-bound per navigation.
 */
import { type RefObject, useEffect, useRef } from "react";
import {
  closeFallbackFor,
  useAppSearch,
  useCloseLayer,
  useOpenLayer,
} from "../../app/layers";

const EDITABLE =
  "input, textarea, select, [contenteditable=''], [contenteditable='true']";

/** Whether `event` is the search chord. Exported for the test. */
export function isSearchChord(event: {
  altKey: boolean;
  ctrlKey: boolean;
  key: string;
  metaKey: boolean;
  shiftKey: boolean;
}): boolean {
  return (
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    !event.shiftKey &&
    event.key.toLowerCase() === "k"
  );
}

/** Whether the keystroke is being typed into a field. */
export function isTyping(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(EDITABLE) !== null;
}

interface Latest {
  closeLayer: ReturnType<typeof useCloseLayer>;
  openLayer: ReturnType<typeof useOpenLayer>;
  searchOpen: boolean;
}

/**
 * Reads the ref through a call: Biome's `noUnnecessaryConditions` narrows
 * `ref.current` to its initial value and calls the check "always falsy".
 */
function read(ref: RefObject<Latest>): Latest {
  return ref.current;
}

export function useSearchShortcut(): void {
  const search = useAppSearch();
  const openLayer = useOpenLayer();
  const closeLayer = useCloseLayer();
  const searchOpen = search.overlay === "search";
  const latest = useRef<Latest>({ closeLayer, openLayer, searchOpen });
  // Refreshed after each commit, never during render (React Compiler).
  useEffect(() => {
    latest.current = { closeLayer, openLayer, searchOpen };
  }, [closeLayer, openLayer, searchOpen]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isSearchChord(event)) {
        return;
      }
      const current = read(latest);
      if (current.searchOpen) {
        event.preventDefault();
        current.closeLayer(closeFallbackFor("overlay"));
        return;
      }
      if (isTyping(event.target)) {
        return;
      }
      event.preventDefault();
      current.openLayer({
        overlay: "search",
        section: undefined,
        edit: undefined,
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
